import { useFeatureIsOn } from 'growthbook'
import { useWorkspaceQuery } from '@repo/studio-hooks-auth/use-workspace-query'
import { Coin } from '@repo/studio-design-system-ui-coin'

import { Loading01Icon } from '@repo/studio-design-system-ui-icons/general/loading-01'
import {
  getCoinBalanceNumberQueryOptions,
  saveCoinBalanceToStorage,
  useCoinBalanceUpdatedSubscription,
} from '@repo/studio-hooks-coins/use-coin-balance'
import { useAnimatedNumber } from 'hooks/use-animated-number'
import { useCoinTransferAnimation } from '@repo/studio-hooks-coins/hooks/use-coin-transfer-animation'
import { ROUTES } from '@repo/studio-utils/routes'
import { useSuspenseQuery } from '@tanstack/react-query'
import { m } from 'framer-motion'
import * as React from 'react'
import { tw } from 'utils/classnames'
import { formatNumberToNumericalAbreviation } from 'utils/number'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { StudioLayoutLink } from '@repo/studio-config/studio-config-router'
import { COIN_COUNT_LOW_THRESHOLD, COIN_COUNT_VARIANT } from './features'

const TeamCoinPoolsPreview = React.lazy(() =>
  import('@repo/studio-components-workspace-switcher/team-coin-pools-preview').then(module => ({
    default: module.TeamCoinPoolsPreview,
  })),
)

const COIN_TEXT_CLASS =
  'typography-14 h-6 flex-shrink-0 items-center justify-end transition-colors group-hover:text-violet-200'

export interface CoinBalanceLabels {
  ariaLabel: string
  formatAmount?: (value: number) => string
}

export function CoinBalance({
  className,
  labels,
  to = ROUTES.COINS.ROOT,
}: {
  className?: string
  labels?: CoinBalanceLabels
  to?: string
}) {
  const teamWorkspacesEnabled = useFeatureIsOn('studio-team-workspaces')
  const { data: workspaceId } = useWorkspaceQuery()
  const t = useStudioTranslate(false)
  const { registerTarget } = useCoinTransferAnimation()
  const variant = COIN_COUNT_VARIANT.alwaysHidden
  const isHideable = true

  const anchorRef = React.useCallback(
    (node: HTMLAnchorElement | null) => {
      registerTarget(node)
    },
    [registerTarget],
  )

  if (teamWorkspacesEnabled && workspaceId) {
    return (
      <React.Suspense fallback={<Coin className="size-6" />}>
        <TeamCoinPoolsPreview
          key={workspaceId}
          teamId={workspaceId}
          className={className}
          to={to}
        />
      </React.Suspense>
    )
  }

  return (
    <m.span initial="rest" animate="rest" whileHover="jump">
      <StudioLayoutLink
        to={to}
        className={tw(
          'group flex max-h-8 cursor-pointer relative items-center justify-end gap-0 text-gray-200 transition-colors hover:text-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-500',
          className,
        )}
        ref={anchorRef}
        aria-label={labels?.ariaLabel ?? t?.('studio.components.coin_balance.aria_label') ?? ''}
      >
        <div className="flex h-6 flex-shrink-0 items-center justify-end">
          <m.span variants={iconVariants}>
            <Coin className="min-h-6 min-w-6" />
          </m.span>
        </div>
        <CoinCountText
          formatAmount={labels?.formatAmount}
          variant={variant}
          isHideable={isHideable}
        />
      </StudioLayoutLink>
    </m.span>
  )
}

// The entire hide/show decision is made inside the Suspense boundary by
// ResponsiveBalance, which already owns the single suspense query observer.
// This eliminates the second (non-suspense) observer that useShouldHideCoinCount
// used to create — that two-observer configuration could trigger an infinite
// render/suspend loop under timing pressure (GROW-3169).
function CoinCountText({
  formatAmount,
  variant,
  isHideable,
}: {
  formatAmount?: (value: number) => string
  variant: number
  isHideable: boolean
}) {
  return (
    <React.Suspense
      fallback={
        isHideable ? null : (
          <>
            <span className={tw(COIN_TEXT_CLASS, 'no-display sm:flex')}>
              <Loading01Icon className="h-4 w-4 animate-spin" />
            </span>
            <span className={tw(COIN_TEXT_CLASS, 'flex sm:no-display')}>
              <Loading01Icon className="h-4 w-4 animate-spin" />
            </span>
          </>
        )
      }
    >
      <ResponsiveBalance formatAmount={formatAmount} variant={variant} />
    </React.Suspense>
  )
}

// `rest` has to spell out `y: 0`. Without a resting value for `y` in a
// lower-priority variant, framer-motion has no base target to fall back to when
// the hover ends, so it leaves the coin frozen wherever the looping keyframes
// happened to be when the pointer left.
const iconVariants = {
  rest: {
    y: 0,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    } as const,
  },
  jump: {
    y: [0, -6, 0, -2, 0],
    transition: {
      duration: 0.6,
      ease: 'easeOut',
      repeat: Infinity,
      repeatDelay: 0.25,
    } as const,
  },
}

// Single suspense observer for the userBalance query — the ONLY observer on this
// key in the component tree. All balance reads (display, hide/show logic) flow
// through this hook. Never add a second observer; see the note on CoinCountText.
function useSuspenseCoinBalance() {
  const workspaceId = useCoinBalanceUpdatedSubscription()

  const userBalanceQuery = useSuspenseQuery(getCoinBalanceNumberQueryOptions(workspaceId))

  React.useEffect(() => {
    if (workspaceId === null) {
      saveCoinBalanceToStorage(userBalanceQuery.data)
    }
  }, [workspaceId, userBalanceQuery.data])

  return userBalanceQuery.data
}

export function Balance({
  abbreviated,
  formatAmount,
}: { abbreviated?: boolean; formatAmount?: (value: number) => string } = {}) {
  const balance = useSuspenseCoinBalance()

  const formatBalance = React.useCallback(
    (val: number) => {
      const rounded = Math.round(val)
      if (abbreviated) {
        return formatNumberToNumericalAbreviation(rounded)
      }
      if (formatAmount) {
        return formatAmount(rounded)
      }
      return rounded.toLocaleString()
    },
    [abbreviated, formatAmount],
  )

  return useAnimatedNumber({
    value: balance,
    duration: 1000,
    format: formatBalance,
  })
}

function shouldHideCoinCount(variant: number, balance: number) {
  if (variant === COIN_COUNT_VARIANT.alwaysHidden) {
    return true
  }
  if (variant === COIN_COUNT_VARIANT.hiddenUnlessLowBalance) {
    return balance > COIN_COUNT_LOW_THRESHOLD
  }
  return false
}

// Hover-gated grid wrapper. On touch devices the count stays icon-only and the
// tap opens /coins. On pointer devices it expands on hover. Spacing is applied
// here (not via flex gap) so the collapsed state doesn't reserve a gap slot.
const HOVER_REVEAL_CLASS =
  'grid grid-cols-[0fr] transition-[grid-template-columns,margin] duration-200 ease-out pointer:group-hover:grid-cols-[1fr] pointer:group-hover:ml-2'

// Renders both the full and abbreviated balances from ONE suspense observer and
// handles hide/show logic inline — no second query observer needed.
function ResponsiveBalance({
  formatAmount,
  variant,
}: {
  formatAmount?: (value: number) => string
  variant: number
}) {
  const balance = useSuspenseCoinBalance()
  const shouldHide = shouldHideCoinCount(variant, balance)

  const formatFull = React.useCallback(
    (val: number) => {
      const rounded = Math.round(val)
      return formatAmount ? formatAmount(rounded) : rounded.toLocaleString()
    },
    [formatAmount],
  )

  const formatAbbreviated = React.useCallback(
    (val: number) => formatNumberToNumericalAbreviation(Math.round(val)),
    [],
  )

  const full = useAnimatedNumber({ value: balance, duration: 1000, format: formatFull })
  const abbreviated = useAnimatedNumber({
    value: balance,
    duration: 1000,
    format: formatAbbreviated,
  })

  const balanceSpans = (
    <>
      <span className={tw(COIN_TEXT_CLASS, 'no-display sm:flex')}>{full}</span>
      <span className={tw(COIN_TEXT_CLASS, 'flex sm:no-display')}>{abbreviated}</span>
    </>
  )

  if (shouldHide) {
    return (
      <span className={HOVER_REVEAL_CLASS}>
        <span className="flex min-w-0 justify-end overflow-hidden">{balanceSpans}</span>
      </span>
    )
  }

  return <span className="ml-2">{balanceSpans}</span>
}
